import { useState } from "react";

import type { ChartRange, ChartResponse, HybridResult } from "../api/types";
import type { ChartType } from "../lib/chartTypes";
import type { IndicatorKey } from "../lib/indicators";
import type { ResolvedTheme } from "../theme/ThemeProvider";
import { ChartToolbar } from "./ChartToolbar";
import { IndicatorChips } from "./IndicatorChips";
import { PriceChart } from "./PriceChart";
import { QuoteHeader } from "./QuoteHeader";
import { RangeTabs } from "./RangeTabs";

interface MarketPanelProps {
  companyName: string;
  symbol: string;
  chart: ChartResponse | null;
  chartStatus: string;
  chartIsError: boolean;
  chartLoading: boolean;
  agentResult: HybridResult | null;
  resolvedTheme: ResolvedTheme;
  ranges: ChartRange[];
  activeRange: string;
  onRangeSelect: (range: string) => void;
  chartType: ChartType;
  onChartTypeChange: (type: ChartType) => void;
  activeIndicators: IndicatorKey[];
  onAddIndicator: (indicator: IndicatorKey) => void;
  onRemoveIndicator: (indicator: IndicatorKey) => void;
}

export function MarketPanel({
  companyName,
  symbol,
  chart,
  chartStatus,
  chartIsError,
  chartLoading,
  agentResult,
  resolvedTheme,
  ranges,
  activeRange,
  onRangeSelect,
  chartType,
  onChartTypeChange,
  activeIndicators,
  onAddIndicator,
  onRemoveIndicator,
}: MarketPanelProps) {
  const [resetNonce, setResetNonce] = useState(0);

  return (
    <section className="market-panel" aria-label="Market chart">
      <QuoteHeader
        companyName={companyName}
        symbol={symbol}
        chart={chart}
        signal={agentResult?.signal}
      />
      <RangeTabs ranges={ranges} activeRange={activeRange} onSelect={onRangeSelect} />
      <ChartToolbar
        chartType={chartType}
        activeIndicators={activeIndicators}
        onChartTypeChange={onChartTypeChange}
        onAddIndicator={onAddIndicator}
        onReset={() => setResetNonce((value) => value + 1)}
      />
      <IndicatorChips activeIndicators={activeIndicators} onRemove={onRemoveIndicator} />
      <div className={`chart-status${chartIsError ? " is-error" : ""}`} role="status">
        {chartStatus}
      </div>
      <PriceChart
        chart={chart}
        chartType={chartType}
        activeIndicators={activeIndicators}
        agentResult={agentResult}
        resolvedTheme={resolvedTheme}
        resetNonce={resetNonce}
        isLoading={chartLoading}
      />
      <p className="chart-attribution">Powered by TradingView Lightweight Charts</p>
    </section>
  );
}
